/**
 * @fileoverview Command Pattern Implementation for Undo/Redo Operations
 * 
 * This file implements the Command pattern to enable undo/redo functionality
 * throughout the application. Commands encapsulate operations that can be
 * executed, undone, and redone, providing a robust history mechanism.
 * 
 * Key Features:
 * - Undo/Redo support for all operations
 * - Command merging for smooth user experience
 * - Composite commands for batch operations
 * - Validation and error handling
 * - Metadata support for debugging
 * 
 * @author Claude Code
 * @version 1.0.0
 */

/**
 * Interface defining a command that can be executed, undone, and redone.
 * 
 * Commands represent operations in the application that can be reversed.
 * They encapsulate both the operation and its inverse, enabling robust
 * undo/redo functionality.
 * 
 * @example
 * ```typescript
 * class MoveNodeCommand implements ICommand {
 *   execute() { moveNode(this.nodeId, this.newPosition); }
 *   undo() { moveNode(this.nodeId, this.oldPosition); }
 * }
 * ```
 */
export interface CommandHistoryOptions {
  /** When true, the command does not participate in undo/redo history */
  skipHistory?: boolean;
  /**
   * Presentation commands change visual state; annotation commands create
   * collaboration data outside the diagram document. Neither changes canvas
   * facts or planning drafts.
   */
  executionScope?: 'document' | 'presentation' | 'annotation';
  requiresWriteAccess?: boolean;
  /**
   * Which authority owns an authored mutation. Commands default to the
   * canvas/Yjs scenario lane; protected-plan commands are server-backed and
   * must never cause the live-chart guard to create a second canvas draft.
   */
  mutationDestination?: 'canvas_scenario' | 'protected_plan' | 'canonical';
}

export interface ICommand {
  /** Stable, value-free identity for retrying this command's server history. */
  readonly historyRecoveryId?: string;
  /** Unique identifier for this command instance */
  id: string;
  
  /** Human-readable name of the command (e.g., "Move Node") */
  name: string;
  
  /** Optional detailed description of what the command does */
  description?: string;
  
  /** Timestamp when the command was created (for merging logic) */
  timestamp: number;
  
  // Core command methods
  
  /** Execute the command's operation */
  execute(): void | Promise<void>;
  
  /** Reverse the command's operation */
  undo(): void | Promise<void>;
  
  /** Re-execute the command (typically same as execute) */
  redo(): void | Promise<void>;
  
  // Optional validation
  
  /** Check if the command can currently be executed */
  canExecute?(): boolean;
  
  /** Check if the command can currently be undone */
  canUndo?(): boolean;
  
  /** Check if the command can currently be redone */
  canRedo?(): boolean;
  
  // For composing/merging commands
  
  /** 
   * Check if this command can be merged with another command.
   * Used to create smooth operations (e.g., merging multiple move commands).
   */
  canMergeWith?(other: ICommand): boolean;
  
  /** 
   * Merge this command with another command.
   * Returns a new command that represents both operations.
   */
  mergeWith?(other: ICommand): ICommand;
  
  // Metadata
  
  /** Get metadata about this command for debugging/logging */
  getMetadata?(): Record<string, any>;
  
  /** Get a string representation of this command */
  toString?(): string;
  
  /** Whether this command should be excluded from undo/redo history */
  skipHistory?: boolean;
  /** What state this command changes; absent means an authored document mutation. */
  executionScope?: 'document' | 'presentation' | 'annotation';
  /** Persisted presentation must obey the document's write and history guards. */
  requiresWriteAccess?: boolean;
  mutationDestination?: 'canvas_scenario' | 'protected_plan' | 'canonical';
}

/**
 * Abstract base class that provides default implementations for ICommand.
 * 
 * This class handles common command functionality like ID generation,
 * timestamp tracking, and default implementations of optional methods.
 * Subclasses only need to implement execute() and undo().
 * 
 * Features:
 * - Automatic unique ID generation
 * - Timestamp tracking for command merging
 * - Default implementations for validation methods
 * - Metadata collection for debugging
 * - String representation for logging
 * 
 * @example
 * ```typescript
 * class DeleteNodeCommand extends BaseCommand {
 *   constructor(nodeId: string) {
 *     super('Delete Node', `Delete node ${nodeId}`);
 *     this.nodeId = nodeId;
 *   }
 *   
 *   execute() { this.deleteNode(this.nodeId); }
 *   undo() { this.restoreNode(this.nodeData); }
 * }
 * ```
 */
export abstract class BaseCommand implements ICommand {
  /** Unique identifier for this command instance */
  id: string;
  
  /** Human-readable name of the command */
  name: string;
  
  /** Optional detailed description */
  description?: string;
  
  /** Timestamp when command was created */
  timestamp: number;
  /** Whether this command should be skipped in history */
  skipHistory?: boolean;
  /** Whether this changes authored document state, viewer presentation, or collaboration annotations. */
  executionScope?: 'document' | 'presentation' | 'annotation';
  requiresWriteAccess?: boolean;
  /** Authority that owns the mutation; absent preserves legacy canvas behavior. */
  mutationDestination?: 'canvas_scenario' | 'protected_plan' | 'canonical';
  
  /** 
   * Creates a new command with automatic ID generation.
   * 
   * @param name - Human-readable name for the command
   * @param description - Optional detailed description
   * @param options - Additional behavior controls
   */
  constructor(name: string, description?: string, options?: CommandHistoryOptions) {
    // Generate unique ID: name-timestamp-randomstring
    this.id = `${name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.name = name;
    this.description = description;
    this.timestamp = Date.now();
    this.skipHistory = options?.skipHistory ?? false;
    this.executionScope = options?.executionScope ?? 'document';
    this.requiresWriteAccess = options?.requiresWriteAccess;
    this.mutationDestination = options?.mutationDestination;
  }
  
  /** Execute the command's operation - must be implemented by subclasses */
  abstract execute(): void | Promise<void>;
  
  /** Undo the command's operation - must be implemented by subclasses */
  abstract undo(): void | Promise<void>;
  
  /** 
   * Re-execute the command. Default implementation calls execute().
   * Override if redo logic differs from initial execution.
   */
  redo(): void | Promise<void> {
    return this.execute();
  }
  
  /** Check if command can be executed. Default: always true. */
  canExecute(): boolean {
    return true;
  }
  
  /** Check if command can be undone. Default: always true. */
  canUndo(): boolean {
    return true;
  }
  
  /** Check if command can be redone. Default: always true. */
  canRedo(): boolean {
    return true;
  }
  
  /** 
   * Check if this command can merge with another. Default: false.
   * Override to enable command merging for smooth user interactions.
   */
  canMergeWith(_other: ICommand): boolean {
    return false;
  }
  
  /** 
   * Merge this command with another. Default: throws error.
   * Override along with canMergeWith() to enable merging.
   */
  mergeWith(_other: ICommand): ICommand {
    throw new Error('Merging not supported for this command');
  }
  
  /** Get metadata about this command for debugging and logging */
  getMetadata(): Record<string, any> {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      timestamp: this.timestamp
    };
  }
  
  /** Get string representation for debugging */
  toString(): string {
    return `${this.name} (${this.id})`;
  }
}

/**
 * Composite command that groups multiple commands into a single operation.
 * 
 * This enables batch operations where multiple commands should be treated
 * as a single undoable unit. Useful for complex operations that involve
 * multiple steps.
 * 
 * Features:
 * - Executes commands in order
 * - Undoes commands in reverse order
 * - Validates all commands before execution
 * - Provides command introspection
 * - Maintains transactional semantics
 * 
 * @example
 * ```typescript
 * const batch = new CompositeCommand('Move Multiple Nodes', [
 *   new MoveNodeCommand('node1', pos1),
 *   new MoveNodeCommand('node2', pos2),
 *   new MoveNodeCommand('node3', pos3)
 * ]);
 * 
 * // All nodes move together as one operation
 * await batch.execute();
 * 
 * // All moves are undone together
 * await batch.undo();
 * ```
 */
export class CompositeCommand extends BaseCommand {
  /** Array of commands to execute as a group */
  private commands: ICommand[];
  
  /**
   * Creates a composite command from multiple individual commands.
   * 
   * @param name - Name for the composite operation
   * @param commands - Array of commands to group together
   * @param description - Optional description of the batch operation
   */
  constructor(name: string, commands: ICommand[], description?: string) {
    super(name, description);
    this.commands = commands;
    if (commands.length > 0 && commands.every((command) => command.executionScope === 'presentation')) {
      this.executionScope = 'presentation';
      this.requiresWriteAccess = commands.some((command) => command.requiresWriteAccess);
    }
  }
  
  /**
   * Execute all commands in order.
   * If any command fails, the composite command stops execution.
   */
  async execute(): Promise<void> {
    for (const command of this.commands) {
      await command.execute();
    }
  }
  
  /**
   * Undo all commands in reverse order.
   * This maintains proper state consistency when undoing batch operations.
   */
  async undo(): Promise<void> {
    // Undo in reverse order to maintain consistency
    for (let i = this.commands.length - 1; i >= 0; i--) {
      await this.commands[i].undo();
    }
  }
  
  /**
   * Redo all commands in original order.
   */
  async redo(): Promise<void> {
    for (const command of this.commands) {
      await command.redo();
    }
  }
  
  /**
   * Check if all commands can be executed.
   * Returns false if any command cannot be executed.
   */
  canExecute(): boolean {
    return this.commands.every(cmd => !cmd.canExecute || cmd.canExecute());
  }
  
  /**
   * Check if all commands can be undone.
   * Returns false if any command cannot be undone.
   */
  canUndo(): boolean {
    return this.commands.every(cmd => !cmd.canUndo || cmd.canUndo());
  }
  
  /**
   * Get a copy of the commands array for introspection.
   * 
   * @returns Array of commands in this composite
   */
  getCommands(): ICommand[] {
    return [...this.commands];
  }
  
  /**
   * Add a command to the end of the composite.
   * 
   * @param command - Command to add to the batch
   */
  addCommand(command: ICommand): void {
    this.commands.push(command);
  }
  
  /**
   * Get enhanced metadata including information about sub-commands.
   */
  getMetadata(): Record<string, any> {
    return {
      ...super.getMetadata(),
      commandCount: this.commands.length,
      commands: this.commands.map(cmd => cmd.getMetadata ? cmd.getMetadata() : { name: cmd.name })
    };
  }
}
